import { createMiddleware, createStart } from '@tanstack/react-start';
import {
  ATTRIBUTION_COOKIE,
  attributionCookieHeader,
  cookieValue,
  isAttributableRequest,
  nextAttribution,
  parseAttributionCookie,
  partnerFromRequest,
  touchFromRequest,
} from './lib/attribution.js';

/**
 * Remember where every visitor came from — see `lib/attribution.ts`.
 *
 * Runs on the document request, after the page has rendered, and only adds a
 * header: the page is never delayed and never fails on account of it. A
 * `Set-Cookie` is written on the first visit and on a later arrival from a
 * new campaign; every other page view is passed through untouched, so the
 * sixty-day expiry counts from the visit that set it.
 */
const rememberArrival = createMiddleware({ type: 'request' }).server(
  async ({ request, handlerType, next }) => {
    const result = await next();

    if (handlerType !== 'router' || !isAttributableRequest(request)) return result;

    try {
      const url = new URL(request.url);
      const existing = parseAttributionCookie(
        cookieValue(request.headers.get('cookie'), ATTRIBUTION_COOKIE)
      );
      const touch = touchFromRequest(url, request.headers.get('referer'));
      const record = nextAttribution(existing, touch, partnerFromRequest(url));
      if (!record) return result;

      const header = attributionCookieHeader(record, {
        host: request.headers.get('host'),
        // Behind a proxy the request reaches us over plain HTTP; the
        // forwarded protocol is what the browser actually used.
        secure:
          url.protocol === 'https:' ||
          request.headers.get('x-forwarded-proto')?.split(',')[0]?.trim() === 'https',
      });

      result.response.headers.append('set-cookie', header);
    } catch (error) {
      // Attribution is worth nothing next to serving the page.
      console.warn('[attribution] could not set the cookie', error);
    }

    return result;
  }
);

export const startInstance = createStart(() => ({
  requestMiddleware: [rememberArrival],
}));
